import { useTranslation } from 'react-i18next';

import { ExecutionOutputs } from '@/modules/outputs/components/ExecutionOutputs';
import { ChatActivity } from '@/shared/components/ChatActivity';

import type { TRainToolCallStatus } from '../../components/RainToolCallCard';
import type { TRunCodeExecutionStatus } from '../quill-run-result';

export interface IRainRunCodeCardProps {
  /** `run_code`'s `purpose`. Absent until it has streamed, or on an older agent. */
  purpose?: string;
  /** Whether the tool call resolved, not how the execution went. */
  status: TRainToolCallStatus;
  /** What the execution reported. A SPy error resolves the call successfully. */
  executionStatus?: TRunCodeExecutionStatus;
  /** Set only when the execution produced `set_output()` values to fetch. */
  outputsExecutionId?: string;
}

/**
 * One `run_code` call — quill2's sandbox probe — as a compact activity row that
 * grows an outputs grid when the snippet published something. Nothing about the
 * snippet is renderable: its source, inputs, ids and error text are all SPy or
 * opaque, so a failed probe shows only that it failed.
 */
export function RainRunCodeCard({
  purpose,
  status,
  executionStatus,
  outputsExecutionId,
}: IRainRunCodeCardProps) {
  const { t } = useTranslation('automation');

  const isRunning = status === 'running';
  const isFailed = status === 'error' || executionStatus === 'FAILED';
  const isStopped = !isFailed && executionStatus === 'STOPPED';

  const label =
    purpose ??
    t('automation.quill2.runCode.fallbackLabel', { ns: 'automation' });

  // The purpose is a present participle, so a settled row keeps the same phrase
  // and only the two non-plain outcomes append to it.
  let title = label;
  if (isFailed) {
    title = t('automation.quill2.runCode.failed', { value: label });
  } else if (isStopped) {
    title = t('automation.quill2.runCode.stopped', { value: label });
  }

  const showOutputs = Boolean(outputsExecutionId) && !isFailed;

  return (
    <ChatActivity
      variant="tool-call"
      compact
      title={title}
      isActive={isRunning}
      isError={isFailed}
      icon={isRunning ? 'Loader2' : 'FlaskConical'}
      collapsible={showOutputs}
      defaultExpanded={false}
      // Never close a pane the reader opened themselves.
      collapseOnComplete={false}
      // An output card is taller than the body's default cap and brings its own
      // scroller, and it paints its own surface — so the body's tint would only
      // add a second panel behind it. Its padding stays, for width parity with
      // every other card.
      bodyClassName="max-h-none bg-transparent"
      data-testid="rain-run-code-card"
      data-status={status}
      data-execution-status={executionStatus ?? ''}
    >
      {showOutputs && outputsExecutionId && (
        <ExecutionOutputs executionId={outputsExecutionId} />
      )}
    </ChatActivity>
  );
}
